/**
 * PhotoTagModal — single-photo tagging modal with prev/next navigation.
 *
 * Props:
 *   photos: Photo[]           — currently loaded page of photos
 *   initialIndex: number      — which photo to show first
 *   total: number             — total photos across all pages
 *   onClose: () => void
 *   onLoadMore: () => Promise<Photo[]>  — fetch next page, returns appended photos
 *   onTagsChanged: (photoId, tags) => void  — notify parent of tag changes
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Users, Search, Plus, BookOpen, ArrowRight } from 'lucide-react';
import { axiosInstance } from '../../services/api';
import { searchPeople } from '../../services/videoActivationAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const AVATAR_COLORS = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];
const avatarColor = (name) => AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];

export default function PhotoTagModal({ photos, initialIndex, total, onClose, onLoadMore, onTagsChanged, onDismissTagging }) {
  const [index, setIndex] = useState(initialIndex);
  const [localPhotos, setLocalPhotos] = useState(photos);
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const photo = localPhotos[index];

  // Keep localPhotos in sync with parent
  useEffect(() => {
    setLocalPhotos(photos);
  }, [photos]);

  // Load tags for current photo
  useEffect(() => {
    if (!photo) return;
    // Use photo.tags if available (from gallery data), otherwise fetch
    if (photo.tags) {
      setTags(photo.tags);
      return;
    }
    setLoadingTags(true);
    axiosInstance.get(`/media/files/${photo.id}/tagged-people/`)
      .then(r => setTags(r.data.people || []))
      .catch(() => setTags([]))
      .finally(() => setLoadingTags(false));
  }, [photo]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const goPrev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const goNext = useCallback(async () => {
    if (index < localPhotos.length - 1) {
      setIndex(index + 1);
    } else if (index < total - 1 && onLoadMore) {
      // At edge of loaded page — fetch more
      const morePhotos = await onLoadMore();
      if (morePhotos && morePhotos.length > localPhotos.length) {
        setLocalPhotos(morePhotos);
        setIndex(index + 1);
      }
    }
  }, [index, localPhotos.length, total, onLoadMore]);

  // Search people — load roster immediately on open, debounce typed queries
  useEffect(() => {
    if (!pickerOpen) return;
    clearTimeout(searchTimer.current);
    const delay = search ? 250 : 0;
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchPeople(search, 20);
        const taggedIds = new Set(tags.map(t => t.person_id || t.id));
        setSearchResults((data.people || []).filter(p => !taggedIds.has(p.id)));
      } catch { setSearchResults([]); }
      setSearching(false);
    }, delay);
    return () => clearTimeout(searchTimer.current);
  }, [search, pickerOpen, tags]);

  const addTag = async (person) => {
    try {
      await axiosInstance.post(`/media/files/${photo.id}/tag-person/`, { person_id: person.id });
      const newTag = { person_id: person.id, id: person.id, name: person.name, initial: person.initial };
      const updated = [...tags, newTag];
      setTags(updated);
      setPickerOpen(false);
      setSearch('');
      // Update photo.tags in localPhotos
      const updatedPhotos = [...localPhotos];
      updatedPhotos[index] = { ...updatedPhotos[index], tags: updated };
      setLocalPhotos(updatedPhotos);
      onTagsChanged?.(photo.id, updated);
    } catch { /* ignore */ }
  };

  const removeTag = async (personId) => {
    try {
      await axiosInstance.delete(`/media/files/${photo.id}/tag-person/${personId}/`);
      const updated = tags.filter(t => (t.person_id || t.id) !== personId);
      setTags(updated);
      const updatedPhotos = [...localPhotos];
      updatedPhotos[index] = { ...updatedPhotos[index], tags: updated };
      setLocalPhotos(updatedPhotos);
      onTagsChanged?.(photo.id, updated);
    } catch { /* ignore */ }
  };

  if (!photo) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(28,32,22,.55)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 20, maxWidth: 560, width: '95vw',
        boxShadow: '0 30px 80px rgba(24,28,16,.4)', overflow: 'hidden',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid #EFEDE2',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={goPrev} disabled={index === 0} style={{
              width: 38, height: 38, borderRadius: '50%', border: '1px solid #E4E1D2',
              background: index === 0 ? '#F5F4EA' : '#fff', cursor: index === 0 ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: index === 0 ? 0.4 : 1,
            }}><ChevronLeft size={18} color="#2C2F26" /></button>
            <span style={{ fontFamily: FONT_B, fontSize: 15, color: '#6E7562', fontWeight: 600 }}>
              {photo.is_video ? 'Video' : 'Photo'} {index + 1} of {total}
            </span>
            <button onClick={goNext} disabled={index >= total - 1} style={{
              width: 38, height: 38, borderRadius: '50%', border: '1px solid #E4E1D2',
              background: index >= total - 1 ? '#F5F4EA' : '#fff', cursor: index >= total - 1 ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: index >= total - 1 ? 0.4 : 1,
            }}><ChevronRight size={18} color="#2C2F26" /></button>
          </div>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: '#F5F4EA', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><X size={18} color="#6E7562" /></button>
        </div>

        {/* Photo or Video */}
        <div style={{
          background: '#1c2016', display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: 200,
        }}>
          {photo.is_video ? (
            <video
              src={photo.file_url || photo.url}
              controls
              style={{ maxHeight: '46vh', maxWidth: '100%' }}
            />
          ) : (
            <img src={photo.url} alt="" style={{
              maxHeight: '46vh', maxWidth: '100%', objectFit: 'contain',
            }} />
          )}
        </div>

        {/* Tagging area */}
        <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Users size={17} color="#556B2F" />
            <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 17, color: '#2C2F26' }}>
              People in this photo
            </span>
            {tags.length > 0 && (
              <span style={{
                background: '#F0F5E8', color: '#556B2F', fontSize: 12, fontWeight: 700,
                padding: '2px 8px', borderRadius: 999, fontFamily: FONT_B,
              }}>{tags.length}</span>
            )}
          </div>

          {/* Tag chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {tags.map(tag => {
              const pid = tag.person_id || tag.id;
              return (
                <div key={pid} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#F7F6EE', border: '1px solid #E4E1D2', borderRadius: 999,
                  padding: '5px 10px 5px 6px', fontSize: 14, fontFamily: FONT_B,
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%', background: avatarColor(tag.name),
                    color: '#fff', fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{tag.initial || (tag.name || '?')[0].toUpperCase()}</div>
                  <span style={{ color: '#2C2F26', fontWeight: 500 }}>{tag.name}</span>
                  <button onClick={() => removeTag(pid)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center', marginLeft: 2,
                  }}><X size={14} color="#9AA088" /></button>
                </div>
              );
            })}

            {/* Tag someone button */}
            {!pickerOpen && (
              <button onClick={() => setPickerOpen(true)} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'transparent', border: '1.5px dashed #D6D3C2', borderRadius: 999,
                padding: '5px 14px', fontSize: 14, color: '#6E7562', cursor: 'pointer',
                fontFamily: FONT_B, minHeight: 36,
              }}>
                <Plus size={14} /> Tag someone
              </button>
            )}
          </div>

          {/* Roster picker */}
          {pickerOpen && (
            <div style={{
              border: '1px solid #E4E1D2', borderRadius: 14, background: '#fff',
              boxShadow: '0 8px 24px rgba(40,46,28,.1)', overflow: 'hidden',
            }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #EFEDE2' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#F5F4EA', border: '1px solid #E4E1D2', borderRadius: 10,
                  padding: '8px 10px',
                }}>
                  <Search size={15} color="#9AA088" />
                  <input
                    value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search family members..."
                    autoFocus
                    style={{
                      border: 'none', outline: 'none', background: 'transparent',
                      fontSize: 14, fontFamily: FONT_B, color: '#2C2F26', flex: 1,
                    }}
                  />
                </div>
              </div>
              <div style={{ maxHeight: 250, overflowY: 'auto', padding: '4px 6px' }}>
                {searching && (
                  <p style={{ textAlign: 'center', fontSize: 13, color: '#9AA088', padding: 12 }}>Searching...</p>
                )}
                {!searching && searchResults.length === 0 && (
                  <p style={{ textAlign: 'center', fontSize: 13, color: '#9AA088', padding: 12 }}>
                    No matches
                  </p>
                )}
                {searchResults.map(person => (
                  <button key={person.id} onClick={() => addTag(person)} style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '9px 10px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: 'transparent', textAlign: 'left', fontFamily: FONT_B,
                    fontSize: 15, color: '#2C2F26',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F7F6EE'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', background: avatarColor(person.name),
                      color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{person.initial || (person.name || '?')[0].toUpperCase()}</div>
                    <span>{person.name}</span>
                    {person.is_minor && (
                      <span style={{ fontSize: 11, color: '#9AA088', background: '#F5F4EA', padding: '1px 6px', borderRadius: 4 }}>child</span>
                    )}
                  </button>
                ))}
              </div>
              <div style={{ borderTop: '1px solid #EFEDE2', padding: '8px 12px', textAlign: 'right' }}>
                <button onClick={() => { setPickerOpen(false); setSearch(''); }} style={{
                  background: 'none', border: 'none', fontSize: 13, color: '#6E7562',
                  cursor: 'pointer', fontFamily: FONT_B, fontWeight: 600,
                }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Helper line */}
          <p style={{ fontSize: 13, color: '#9AA088', marginTop: 10, fontFamily: FONT_B, margin: '10px 0 0' }}>
            Tagging here also links this photo to each person's own life page.
          </p>

          {/* Story link row */}
          {photo.storyId && (
            <>
              <div style={{ height: 1, background: '#EFEDE2', margin: '14px 0' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: photo.uploader ? 6 : 0 }}>
                <BookOpen size={16} color="#556B2F" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: '#6E7562', fontFamily: FONT_B, flex: 1 }}>
                  From <strong style={{ color: '#2C2F26' }}>{photo.storyTitle || 'Untitled'}</strong>
                </span>
                <a
                  href={`/stories/${photo.storyId}`}
                  onClick={(e) => { e.preventDefault(); window.location.href = `/stories/${photo.storyId}`; }}
                  style={{
                    fontSize: 14, fontWeight: 600, color: '#556B2F', fontFamily: FONT_B,
                    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Open story <ArrowRight size={14} />
                </a>
              </div>
              {photo.uploader && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 24 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: avatarColor(photo.uploader.name),
                    color: '#fff', fontSize: 9, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{photo.uploader.initial}</div>
                  <span style={{ fontSize: 13, color: '#9AA088', fontFamily: FONT_B }}>
                    Shared by {photo.uploader.name}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Dismiss tagging */}
          {onDismissTagging && tags.length === 0 && !photo.tagging_dismissed && (
            <>
              <div style={{ height: 1, background: '#EFEDE2', margin: '14px 0' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: '#9AA088', fontFamily: FONT_B }}>
                  Don't want to tag this one?
                </span>
                <button onClick={() => { onDismissTagging(photo.id); goNext(); }}
                  style={{
                    background: 'none', border: '1px solid #DCD9C8', borderRadius: 8,
                    padding: '6px 12px', fontSize: 13, color: '#6E7562', cursor: 'pointer',
                    fontFamily: FONT_B, fontWeight: 600,
                  }}
                >Don't ask again for this photo</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
